import axios from 'axios';
import { API_CONFIG, buildTenantApiUrl, getAuthHeaders } from '../config/api';

export interface Unit {
  id: number;
  buildingId: number;
  buildingName?: string;
  agentId?: number;
  agentName?: string;
  unitName: string;
  merchantName?: string;
  merchantCountry?: string;
  merchantProvince?: string;
  merchantCity?: string;
  merchantRegion?: string;
  merchantAddress?: string;
  merchantPhone?: string;
  merchantEmail?: string;
  merchantDescription?: string;
  country?: string;
  state?: string;
  city?: string;
  region?: string;
  address?: string;
  layout: number; // 0=studio, 1=1BR, 2=2BR, 3=3BR, 4=4BR
  serviceMode: number; // 1=full management, 2=brokerage
  rentalType: number; // 1=entire unit, 2=shared
  type: number; // 1=Apartment, 2=Condo, 3=Townhouse, 4=Single Family
  area?: number;
  monthlyRent?: number;
  securityDeposit?: string;
  status?: number; // 1=fully rented, 2=partially vacant, 3=fully vacant, 4=frozen
  floor?: number;
  maxOccupancy?: number;
  availableDate?: string;
  leaseStart?: string;
  leaseEnd?: string;
  description?: string;
  highlights?: string;
  totalRooms?: number;
  availableRooms?: number;
  createTime?: string;
  updateTime?: string;
  createBy?: string;
  updateBy?: string;
}

export interface UnitWithRooms extends Unit {
  rooms?: Room[];
}

export interface Room {
  id: number;
  unitId: number;
  roomNo: string;
  roomId?: string;
  price: number;
  securityDeposit?: string;
  status: number; // 1=rented, 2=available, 3=reserved, 4=frozen
  roomtypeName?: string;
  orientation?: string;
  bedSize?: string;
  windowOpenable?: string;
  roomArea?: string;
  leaseStart?: string;
  leaseEnd?: string;
}

export interface UnitSearchParams {
  page?: number;
  size?: number;
  buildingId?: number;
  layout?: number;
  rentalType?: number;
  status?: number;
  minPrice?: number;
  maxPrice?: number;
  city?: string;
  region?: string;
}

export interface PaginatedUnitResponse {
  content: Unit[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
}


/** Standard tenant/public API envelope — GET /public/unit/detail/{unitId} */
export interface TenantApiResponse<T> {
  success: boolean;
  code: string;
  message: string;
  details?: T;
  exceptionRequestId?: string | null;
  exceptionTimestamp?: string | null;
}

/** Unit block inside details — matches latest /public/unit/detail response */
export interface UnitInfoV2 {
  /** Optional merchant details supplied by the unit-detail endpoint. */
  merchantCountry?: string | null;
  merchantProvince?: string | null;
  merchantCity?: string | null;
  merchantRegion?: string | null;
  merchantAddress?: string | null;
  merchantPhone?: string | null;
  merchantEmail?: string | null;
  merchantLogo?: string | null;
  id: number;
  unitNo?: string | null;
  unitName: string;
  agentName?: string | null;
  merchantName?: string | null;
  buildingId: number;
  address: string;
  country: string;
  state: string;
  city: string;
  region: string;
  layout: number;
  layoutDesc: string;
  rentalType: number;
  rentalTypeDesc: string;
  petPolicyDesc?: string | null;
  /** When set, parking row shows Yes/No; null keeps row with no pills */
  parking?: boolean | null;
  parkingDesc?: string | null;
  securityDeposit?: string | number | null;
  firstMonReqd?: string | number | null;
  lastMonReqd?: string | number | null;
  /** Tenant endpoint classification: 1=student housing, 2=market housing; not Apartment/Condo. */
  type: number;
  typeDesc: string;
  serviceMode: number | null;
  serviceModeDesc: string | null;
  status: number;
  statusDesc: string;
  unitArea: number;
  rentPrice: number;
  bedroomCount: number;
  bathroomCount: number;
  isShort?: boolean | string | null;
  isBill?: boolean | string | null;
  isButlerService?: boolean | string | null;
  /** Comma-separated amenity labels (fallback when details.amenities array is empty) */
  amenities?: string | null;
  availableDate?: string | null;
  description?: string | null;
  tenancyTerm?: string | null;
  licenseNo?: string | null;
  merchantIdentity?: number | null;
  merchantNo?: string | null;
  originalEntryTimestamp?: string | null;
  originAgentStatus?: number | null;
  originUpdateTime?: string | null;
  directionFaces?: string | null;
  rentIncludes?: string | null;
  mlsStandardStatus?: string | null;
  mlsOriginalEntryTimes?: string | null;
  listingOriginatingType?: string | null;
  /** Legacy / optional fields — may be absent on newer payloads */
  contactPhone?: string | null;
  phoneNumber?: string | null;
  deposit?: string | null;
  orientation?: string | null;
  floor?: number | null;
}

export interface AgentInfoV2 {
  /** Nexa Me Book with me switch returned by unit detail: 0 = off, 1 = on. */
  bookEnabled?: 0 | 1 | null;
  userId: string | number;
  email: string | null;
  phone: string | null;
  nickname: string | null;
  merchantId?: string | number | null;
  merchantName: string | null;
  avatar: string | null;
}

export interface BuildingInfoV2 {
  id: number;
  name: string;
  address: string;
  description?: string | null;
  /** Comma-separated building-level amenities */
  amenities?: string | null;
  country: string;
  province: string | null;
  city: string;
  region: string;
  builtYear: number | null;
  lon: number;
  lat: number;
  totalFloors: number | null;
  totalBuilding: number | null;
  buildingType: number | null;
  buildingTypeDesc: string | null;
  /** Listing/building subtype used for user-facing property type, e.g. Apartment or Condominium. */
  mlsPropertySubType?: string | null;
  url: string | null;
}

export interface UnitImageV2 {
  id: number;
  imgUrl: string;
  description: string | null;
  displayOrder: number;
}

export interface UnitAmenityV2 {
  dictValue: string;
  dictLabel: string | null;
  /** e.g. "appliances", "Laundry" */
  type?: string | null;
}

export interface UnitDetailV2 {
  unitInfo: UnitInfoV2;
  agentInfo: AgentInfoV2 | null;
  /** Booking permission only; the unrelated agent identity remains quarantined. */
  agentBookingEnabled?: boolean;
  buildingInfo: BuildingInfoV2;
  images: UnitImageV2[];
  amenities: UnitAmenityV2[];
}

const normalizeUnitDetail = (details: UnitDetailV2): UnitDetailV2 => {
  const unitInfo = details.unitInfo;
  const sortedImages = [...(details.images ?? [])].sort(
    (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
  );

  return {
    ...details,
    // Temporarily barred: this agent may be unrelated to the listing merchant.
    // Block identity, contacts and IDs for every unit-detail consumer.
    agentInfo: null,
    agentBookingEnabled: details.agentInfo?.bookEnabled !== 0,
    unitInfo: {
      ...unitInfo,
      orientation: unitInfo.orientation ?? unitInfo.directionFaces ?? null,
      amenities: unitInfo.amenities ?? null,
    },
    images: sortedImages,
    amenities: details.amenities ?? [],
  };
};

// ─── Tenant Application ───────────────────────────────────────────────────────

export interface CoSignerDTO {
  /** Combined full name */
  fullName: string;
  /** Dialing area code (e.g. "1") */
  areaCode?: string;
  /** Phone number (≤20 chars) */
  phoneNumber: string;
  /** Email address (≤100 chars) */
  email: string;
}

export interface TenantApplicationRequest {
  /** Required: unit being applied for */
  unitId: number;
  /** Required: 1=individual, 2=with guarantor, 3=with roommate */
  applicationType: number;
  firstName: string;
  lastName: string;
  email: string;
  /** Dialing area code (e.g. "1") */
  areaCode?: string;
  phoneNumber: string;
  /** Format: yyyy-MM-dd */
  dateOfBirth: string;
  /** Required: 1=full-time, 2=student, 3=part-time, 4=self-employed */
  employmentStatus: number;
  /** Format: yyyy-MM-dd */
  leaseStartDate: string;
  leaseTermMonths: number;
  /** Auto-filled from unit */
  monthlyRent: number;
  /** Optional: listing agent */
  agentId?: number;
  /** Required when applicationType ≠ 1 */
  coSigners?: CoSignerDTO[];
  annualIncome?: number;
  depositAmount?: number;
  employmentType?: string;
  incomeProofUrl?: string;
  schoolWorkplaceAddress?: string;
}

// ─── Listing Share Email ─────────────────────────────────────────────────────

export interface ListingShareEmailRequest {
  /** Sender name */
  senderName: string;
  /** Sender's personal message, max 200 characters on backend */
  senderMessage: string;
  /** Listing cover image URL */
  listingCover: string;
  /** Price per month */
  price: string;
  /** Beds, baths, and square feet information */
  bedsBaSqft: string;
  /** Listing address */
  address: string;
  /** Move-in date */
  moveIn: string;
  /** Listing highlights */
  highlights: string;
  /** Unit/listing id to view the listing */
  viewListingId: string;
  /** URL to learn more about AllRoomie */
  learnMoreUrl: string;
  /** Recipient email addresses, comma-separated, max 5 emails on backend */
  recipientEmail: string;
}

export type ListingShareEmailResponse = TenantApiResponse<string>;

// ─── My Applications ──────────────────────────────────────────────────────────

export interface MyApplicationsRequest {
  /** Required: page number, 1-based */
  page: number;
  /** Page size (default 5 when omitted) */
  size?: number;
  /** Optional: filter by application status integer */
  status?: number;
}

/** Application status codes from the live Java TenantApplicationListItemVO contract. */
export const APPLICATION_STATUS = {
  PENDING: 1,
  UNDER_REVIEW: 2,
  NEGOTIATING: 3,
  ACCEPTED: 4,
  REJECTED: 5,
  CANCELLED: 6,
} as const;

export type ApplicationStatusCode = typeof APPLICATION_STATUS[keyof typeof APPLICATION_STATUS];

export interface MyApplicationRecord {
  applicationId?: number | null;
  unitId?: number | null;
  propertyAddress?: string | null;
  unitNumber?: string | null;
  monthlyRent?: number | null;
  leaseStartDate?: string | null;
  leaseTermMonths?: number | null;
  /** Application status integer code */
  applicationStatus?: number | null;
  /** Human-readable status label from backend */
  statusText?: string | null;
  submitTime?: string | null;
  unitLocked?: boolean | null;
  lon?: number | null;
  lat?: number | null;
}

export interface MyApplicationsListDetails {
  records: MyApplicationRecord[];
  total: number;
  current: number;
  size: number;
  pages: number;
}

export interface MyApplicationsListResponse {
  success: boolean;
  code: string;
  message: string;
  details?: MyApplicationsListDetails;
  exceptionRequestId?: string | null;
  exceptionTimestamp?: string | null;
}

// ─────────────────────────────────────────────────────────────────────────────

class UnitService {
  // Simple cache to prevent duplicate requests
  private cache = new Map<string, { data: any; timestamp: number }>();
  private cacheTimeout = 30000; // 30 seconds

  private getCacheKey(method: string, params: any): string {
    return `${method}_${JSON.stringify(params)}`;
  }

  private getFromCache<T>(key: string): T | null {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data;
    }
    this.cache.delete(key);
    return null;
  }

  private setCache(key: string, data: any): void {
    this.cache.set(key, { data, timestamp: Date.now() });
  }

  // ─── OLD PMS METHODS (commented out — no active callers) ─────────────────────
  /*
  async getUnitList(params: UnitSearchParams = {}): Promise<PaginatedUnitResponse> {
    // OLD PMS: GET /pms/unit/list — no active callers
  }
  async getUnitsByBuilding(...) { // via getUnitList }
  async getAvailableUnits(...) { // via getUnitList }
  async getUnitsByBuildingId(...) { // via getUnitList — only caller was SimilarUnitsSection (hidden) }
  async getUnitById(id: number): Promise<Unit> {
    // OLD PMS: GET /pms/unit/{id} — replaced by getUnitByIdV2
  }
  */

  /**
   * Get unit details by ID — GET /public/unit/detail/{unitId}
   * Returns details: { unitInfo, agentInfo, buildingInfo, images, amenities }
   */
  async getUnitByIdV2(id: number | string): Promise<UnitDetailV2> {
    try {
      const url = buildTenantApiUrl(`${API_CONFIG.ENDPOINTS.TENANT_UNIT_DETAIL}/${id}`);

      const response = await axios.get<TenantApiResponse<UnitDetailV2>>(url, {
        headers: {
          ...API_CONFIG.HEADERS,
          ...getAuthHeaders(),
        },
        timeout: API_CONFIG.TIMEOUT,
      });

      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Failed to fetch unit details');
      }

      const details = response.data.details;
      if (!details?.unitInfo) {
        throw new Error('Unit detail response missing unitInfo');
      }

      return normalizeUnitDetail(details);
    } catch (error) {
      console.error(`Failed to fetch unit ${id} from Tenant API:`, error);
      throw error;
    }
  }

  /**
   * Get multiple units by their IDs in a single batch request
   * NEW ENDPOINT: Used for fetching saved/favorited units efficiently
   *
   * @param unitIds - Array of unit IDs to fetch
   * @returns Array of unit details from batch API
   */
  async getUnitsBatch(unitIds: number[]): Promise<any[]> {
    try {
      if (!unitIds || unitIds.length === 0) {
        console.log('No unit IDs provided to getUnitsBatch');
        return [];
      }

      const endpoint = '/public/unit/unitsByIds'; // Correct endpoint name
      const url = buildTenantApiUrl(endpoint);

      // Join IDs with comma separator
      const unitIdsParam = unitIds.join(',');

      console.log('🏠 [getUnitsBatch] Fetching units by IDs:', unitIdsParam);
      console.log('🏠 [getUnitsBatch] URL:', url);

      const response = await axios.get(url, {
        params: { unitIds: unitIdsParam }, // Parameter name is 'unitIds'
        headers: {
          ...API_CONFIG.HEADERS,
          ...getAuthHeaders()
        },
        timeout: API_CONFIG.TIMEOUT
      });

      console.log('✅ [getUnitsBatch] Raw response:', response.data);

      // Check if response is successful
      if (!response.data.success) {
        console.warn('⚠️ [getUnitsBatch] API returned unsuccess:', response.data.message);
        return [];
      }

      const units = response.data.details;

      if (!units || !Array.isArray(units)) {
        console.warn('⚠️ [getUnitsBatch] No units array in response details');
        return [];
      }

      console.log(`✅ [getUnitsBatch] Successfully fetched ${units.length} units`);

      // Return raw unit data
      // Structure from backend:
      // {
      //   unitId, buildingId, address, city, lon, lat, mainImageUrl,
      //   layout, layoutDesc, unitArea, rentPrice, bedroomCount, bathroomCount,
      //   amenities, term, availableDate
      // }
      return units;
    } catch (error) {
      console.error('❌ [getUnitsBatch] Failed to fetch units by IDs:', error);
      return [];
    }
  }

  // async getUnitWithRooms — OLD PMS: GET /pms/unit/withRooms/{id} — no active callers

  /**
   * Fetch the logged-in tenant's own applications.
   * GET /tenant/applications/my
   * Query params: current (1-based), size, status (optional)
   * Requires Authorization header.
   */
  async getMyApplications(
    params: MyApplicationsRequest
  ): Promise<MyApplicationsListResponse> {
    const url = buildTenantApiUrl('/tenant/applications/my', {
      current: params.page,
      size: params.size ?? 5,
      ...(params.status !== undefined && { status: params.status }),
    });
    const response = await axios.get<MyApplicationsListResponse>(url, {
      headers: {
        ...API_CONFIG.HEADERS,
        ...getAuthHeaders(),
      },
      timeout: API_CONFIG.TIMEOUT,
    });
    return response.data;
  }

  /** Cancel one of the logged-in tenant's applications. */
  async cancelMyApplication(applicationId: string | number): Promise<TenantApiResponse<boolean>> {
    const id = String(applicationId ?? '').trim();
    if (!id) throw new Error('applicationId is required');
    const url = buildTenantApiUrl(`/tenant/applications/${encodeURIComponent(id)}/cancel`);
    const response = await axios.put<TenantApiResponse<boolean>>(url, undefined, {
      headers: {
        ...API_CONFIG.HEADERS,
        ...getAuthHeaders(),
      },
      timeout: API_CONFIG.TIMEOUT,
    });
    return response.data;
  }

  /**
   * Submit a tenant rental application.
   * POST /api/v1/tenant/tenant/applications
   * Requires a valid auth token.
   */
  async submitApplication(request: TenantApplicationRequest): Promise<any> {
    try {
      const url = buildTenantApiUrl(API_CONFIG.ENDPOINTS.TENANT_APPLICATION_SUBMIT);
      const response = await axios.post(url, request, {
        headers: {
          ...API_CONFIG.HEADERS,
          ...getAuthHeaders(),
        },
        timeout: API_CONFIG.TIMEOUT,
      });

      if (response.data && response.data.success === false) {
        throw new Error(response.data.message || 'Failed to submit application');
      }

      return response.data;
    } catch (error) {
      console.error('Failed to submit tenant application:', error);
      throw error;
    }
  }

  /**
   * Share a listing through backend-generated HTML email.
   * POST /api/v1/tenant/tenant/mail/share-listing
   */
  async shareListingByEmail(request: ListingShareEmailRequest): Promise<ListingShareEmailResponse> {
    try {
      const url = buildTenantApiUrl(API_CONFIG.ENDPOINTS.TENANT_SHARE_LISTING_EMAIL);
      const response = await axios.post<ListingShareEmailResponse>(url, request, {
        headers: {
          ...API_CONFIG.HEADERS,
          ...getAuthHeaders(),
        },
        timeout: API_CONFIG.TIMEOUT,
      });

      if (response.data && response.data.success === false) {
        throw new Error(response.data.message || 'Failed to share listing by email');
      }

      return response.data;
    } catch (error) {
      console.error('Failed to share listing by email:', error);
      throw error;
    }
  }

  /**
   * Get layout display name
   */
  getLayoutName(layout: number): string {
    switch (layout) {
      case 0: return 'Studio';
      case 1: return '1 Bedroom';
      case 2: return '2 Bedrooms';
      case 3: return '3 Bedrooms';
      case 4: return '4 Bedrooms';
      case 5: return '5+ Bedrooms';
      default: return 'Unknown';
    }
  }

  /**
   * Get rental type display name
   */
  getRentalTypeName(rentalType: number): string {
    switch (rentalType) {
      case 1: return 'Entire Unit';
      case 2: return 'Shared';
      default: return 'Unknown';
    }
  }

  /**
   * Get unit type display name
   */
  getUnitTypeName(type: number): string {
    switch (type) {
      case 1: return 'Apartment';
      case 2: return 'Condo';
      case 3: return 'Townhouse';
      case 4: return 'Single Family';
      default: return 'Unknown';
    }
  }

  /**
   * Get status display name
   */
  getStatusName(status: number): string {
    switch (status) {
      case 1: return 'Fully Rented';
      case 2: return 'Available';
      case 3: return 'Available';
      case 4: return 'Frozen';
      default: return 'Unknown';
    }
  }

  /**
   * Check if unit is available (partially or fully vacant)
   */
  isUnitAvailable(status: number): boolean {
    return status === 2 || status === 3;
  }

  /**
   * Check if unit is rented
   */
  isUnitRented(status: number): boolean {
    return status === 1;
  }

  // private transformUnit — only used by old PMS methods above, commented out with them
}

const unitService = new UnitService();
export default unitService;
